import type {
  WBudgetItemPaymentView,
  WBudgetItemView,
  WBudgetTaxonomyNodeView,
} from '@zola/svc-web-api-ts-client';

import { ActionTypes, BudgetActionTypes } from './actionTypes';
import { BudgetContextStateType } from './context';

export const budgetPaymentReducer = (
  state: WBudgetItemPaymentView[],
  action: BudgetActionTypes,
  itemUuid: string | undefined
): WBudgetItemPaymentView[] => {
  switch (action.type) {
    case ActionTypes.RECEIVE_PAYMENT: {
      const existingPaymentIndex = state.findIndex(
        payment => payment.uuid === action.payload.payment.uuid
      );
      if (existingPaymentIndex >= 0) {
        return state.map((payment, i) =>
          i === existingPaymentIndex
            ? { ...state[existingPaymentIndex], ...action.payload.payment }
            : payment
        );
      }
      if (itemUuid === action.payload.payment.budget_item_uuid) {
        return [...state, action.payload.payment];
      }
      return state;
    }
    case ActionTypes.RECEIVE_DELETED_PAYMENT: {
      return state.filter(payment => payment.uuid !== action.payload.paymentUuid);
    }
    default:
      return state;
  }
};

export const budgetItemReducer = (
  state: WBudgetItemView[],
  action: BudgetActionTypes,
  taxonomyUuid: string | undefined
): WBudgetItemView[] => {
  switch (action.type) {
    case ActionTypes.RECEIVE_ITEM: {
      const existingItemIndex = state.findIndex(item => item.uuid === action.payload.item.uuid);
      if (existingItemIndex >= 0) {
        return state.map((item, i) =>
          i === existingItemIndex ? { ...state[existingItemIndex], ...action.payload.item } : item
        );
      }
      if (taxonomyUuid === action.payload.item.taxonomy_node_uuid) {
        return [...state, action.payload.item];
      }
      return state;
    }
    case ActionTypes.RECEIVE_DELETED_ITEM: {
      return state.filter(item => item.uuid !== action.payload.itemUuid);
    }
    default:
      return state.map(item => ({
        ...item,
        payments: budgetPaymentReducer(item.payments || [], action, item.uuid),
      }));
  }
};

export const taxonomyNodeReducer = (
  state: WBudgetTaxonomyNodeView[],
  action: BudgetActionTypes
): BudgetContextStateType['budget']['taxonomy_nodes'] => {
  switch (action.type) {
    case ActionTypes.RECEIVE_TAXONOMY_NODES: {
      return action.payload.taxonomyNodes.map(node => ({
        ...node,
        items: budgetItemReducer(node.items || [], action, node.uuid),
      }));
    }
    case ActionTypes.RECEIVE_BUDGET: {
      return action.payload.budget.taxonomy_nodes?.map(node => ({
        ...node,
        items: budgetItemReducer(node.items || [], action, node.uuid),
      }));
    }
    default:
      return state.map(node => ({
        ...node,
        items: budgetItemReducer(node.items || [], action, node.uuid),
      }));
  }
};

export const budgetReducer = (
  state: BudgetContextStateType['budget'],
  action: BudgetActionTypes
): BudgetContextStateType['budget'] => {
  switch (action.type) {
    case ActionTypes.RECEIVE_BUDGET:
      return {
        ...state,
        ...action.payload.budget,
        taxonomy_nodes: taxonomyNodeReducer(state.taxonomy_nodes || [], action),
      };
    default:
      return { ...state, taxonomy_nodes: taxonomyNodeReducer(state.taxonomy_nodes || [], action) };
  }
};

export const unsavedChangesReducer = (
  state: BudgetContextStateType['unsavedChanges'],
  action: BudgetActionTypes
): BudgetContextStateType['unsavedChanges'] => {
  switch (action.type) {
    case ActionTypes.RECEIVE_UNSAVED_MAX_SPEND:
      return {
        ...state,
        unsavedMaxSpend: action.payload.maxSpend,
      };
    case ActionTypes.RECEIVE_UNSAVED_ITEM:
      return {
        ...state,
        unsavedItems: {
          ...state.unsavedItems,
          [action.payload.itemUuid]: action.payload.fields,
        },
      };
    case ActionTypes.RECEIVE_UNSAVED_GROUPS:
      return {
        ...state,
        unsavedGroups: action.payload.groups,
      };
    case ActionTypes.CLEAR_MAX_SPEND_FROM_UNSAVED: {
      const updatedState = { ...state };
      delete updatedState.unsavedMaxSpend;
      return updatedState;
    }
    case ActionTypes.CLEAR_ITEM_FROM_UNSAVED: {
      const { [action.payload.itemUuid]: _, ...rest } = state.unsavedItems;
      return {
        ...state,
        unsavedItems: {
          ...rest,
        },
      };
    }
    case ActionTypes.CLEAR_GROUPS_FROM_UNSAVED: {
      const updatedState = { ...state };
      delete updatedState.unsavedGroups;
      return updatedState;
    }
    default:
      return state;
  }
};

export const budgetAppReducer = (
  state: BudgetContextStateType,
  action: BudgetActionTypes
): BudgetContextStateType => {
  switch (action.type) {
    case ActionTypes.RECEIVE_BUDGET:
      return {
        ...state,
        budget: budgetReducer(state.budget, action),
      };
    case ActionTypes.RECEIVE_BUSY:
      return {
        ...state,
        busy: action.payload.busy,
      };
    case ActionTypes.RECEIVE_ITEM_TYPES:
      return {
        ...state,
        item_types: action.payload.itemTypes,
      };
    default:
      return {
        ...state,
        budget: budgetReducer(state.budget, action),
        unsavedChanges: unsavedChangesReducer(state.unsavedChanges, action),
      };
  }
};
